import {
    Component,
    Element,
    Event,
    EventEmitter,
    h,
    Host,
    Listen,
    Prop,
    State,
    Watch,
} from '@stencil/core';
import { ThemeType } from '@uipath/portal-shell-util';

import { DropdownOption } from '../../models/portalActions';
import store from '../../store/store';
import { getLanguage } from '../../utils/languageHelper';
import { t } from '../../utils/localization/loc';
import Logger from '../../utils/logger/logger';
import { getTheme } from '../../utils/themeHelper';

@Component({
    tag: 'portal-dropdown',
    styleUrl: 'portal-dropdown.scss',
    shadow: true,
})
export class PortalDropdown {

    @Element() $el: HTMLPortalDropdownElement;

    /**
     * Dropdown state.
     * 'open': This will show dropdown list in open state`
     * 'close': This will snot show the list untill dropdown clicked
     */
    @Prop({ mutable: true }) dropdownState: 'open' | 'close' = 'close';

    /**
     * Prefix icon of the dropdown
     * @memberof PortalDropdown
     */
    @Prop() prefixIcon: string;

    /**
     * noBorder prop of the dropdown. If set to true then border will not be visible
     * If 'no-border' attribute is available then it is true. No need to pass any true/false
     * @memberof PortalDropdown
     */
    @Prop() outlined: boolean = true;

    /**
     * Dropdown list alignment corresponding to the dropdown
     * If list width is more than the dropdown then alignment will be from left by default.
     * Setting listAlign to 'right' will make the list aligned to right with dropdown
     * Depending on the dropdown position this can be set accordingly.
     * Ex. If we want to show dropdown in top right corner, listAlign should be 'right' so that it is visible properly
     */
    @Prop() listAlign: 'left' | 'right' = 'left';

    /**
     * Width of the dropdwon
     * @memberof PortalDropdown
     */
    @Prop() width: string;

    /**
     * height of the dropdown list. After which scrollbar will appear if there are more items to show
     * @memberof PortalDropdown
     */
    @Prop() maxListHeight: string = '350px';// default value if no scrollHeight is passed

    /**
     * 'disabled' for the dropdown as prop. If dropdown has disabled attribute,
     * It should not be clickable and options should not show.
     * @memberof PortalDropdown
     */
    @Prop() disabled: boolean = false;

    /**
   * 'Placeholder' for the dropdown as prop. If none passed then default will be added.
   * @memberof PortalDropdown
   */
    @Prop() placeholder: string;

    /**
    * Theme override to bypass store state
    * @memberof PortalDropdown
    */
    @Prop() theme: ThemeType = 'autoTheme';

    /**
     * Dropdown options passed as props
     *  @memberof PortalDropdown
     */
    @Prop() options: DropdownOption[] | string;

    /**
     * Pass the selected value to show it selected/pre-filled in dropdown
     */
    @Prop() selected: string | DropdownOption;

    /**
     * Aria Label
     * @memberof PortalDropdown
     */
    @Prop() ariaLabel: ARIAMixin['ariaLabel'] = '';

    /**
     * Aria labelledby
     * @memberof PortalDropdown
     */
    @Prop() ariaLabelledby: string = '';

    /**
     * Define custom event for option click
     */
    @Event({
        eventName: 'optionSelected',
        composed: true,
        cancelable: true,
        bubbles: true,
    }) optionSelected: EventEmitter;

    /**
     * Private variables
     */
    private $dropdownEl: HTMLElement | null;
    private optionListObj = {};
    private focusedMenuIndex: number = -1;
    private fromKeyboard = true;

    /**
     * State variables
     */
    @State() private selectedOption?: DropdownOption;
    @State() private dropdownOptionList?: DropdownOption[];

    /**
     * Stencil lifecycle method: Watch value change on Actions attribute
     * @param newValue
     */
    @Watch('options')
    arrayDataWatcher(newValue: DropdownOption[] | string) {
        if (typeof newValue === 'string') {
            this.dropdownOptionList = JSON.parse(newValue);
        } else {
            this.dropdownOptionList = newValue;
        }
    }

    /**
     *
     * @param newValue Watch selected value
     */
    @Watch('selected')
    objectDataWatcher(newValue?: DropdownOption | string) {
        if (typeof newValue === 'string') {
            this.selectedOption = JSON.parse(newValue);
        } else {
            this.selectedOption = newValue;
        }
    }

    /**
     * ComponentWillLoad Lifecycle
     */
    componentWillLoad() {
        this.arrayDataWatcher(this.options);
        this.objectDataWatcher(this.selected);
    }

    /**
     * Listening to the outside click to close the dropdown
     * @param ev
     */
    @Listen('click', { target: 'window' })
    handleOutsideClick(ev) {
        const target = ev.target as HTMLElement;
        if (target.tagName.toLocaleLowerCase() !== 'portal-dropdown' && this.isDropdownOpen()) {
            this.closeDropdown();
        }
    }

    /**
     * `componentDidRender` will trigger when component s rendered properly
     *  We are getting the list and setting the active state option
     *  In cases when dropdown state is open, we need to show the selected option
     */
    componentDidRender() {
        const dropDown = this.$el.shadowRoot?.querySelector('.portal-dropdown') as HTMLElement;
        if (dropDown) {
            this.$dropdownEl = dropDown;
        }

        this.getOptionList();
    }

    render() {
        const inlineStyle = {
            'width': this.width ?? '',
            'border': this.outlined ? '' : 'none',
        };

        let liIndex = -1;
        const listCss = {
            'max-height': this.maxListHeight,
            'left': this.listAlign === 'left' ? '0' : 'auto',
            'right': this.listAlign === 'right' ? '0' : 'auto',
        };
        const dropdownClasses = {
            'portal-dropdown': true,
            'dropdown-open': this.dropdownState === 'open',
            'disabled': this.disabled,
        };

        const handleId = this.getHandleId();
        const placeholderText = this.getPlaceholderText();
        const selectedValue = this.getSelectedValue();
        Logger.debug([ 'render', 'portal-dropdown' ], `selectedValue : ${selectedValue}`);

        return (
            <Host class={getTheme(store, this.theme)} >
                <div class="portal-dropdown-container">
                    <div
                        aria-labelledby={handleId}
                        class={dropdownClasses}
                        data-test="portal-dropdown"
                        style={inlineStyle}
                        onClick={(event: Event) => this.toggleDropdown(event)}
                        role="button"
                        aria-expanded={`${this.dropdownState === 'open'}`}
                        aria-activedescendant={null}
                        aria-haspopup="listbox"
                        aria-controls="listbox"
                    >
                        {this.prefixIcon && <portal-icon variant="normal" class="option-icon prefix" name={this.prefixIcon} size="20px"/>}
                        <div data-cy={`dropdown-${this.$el.id}`} class="dropdown-text">{selectedValue ?? placeholderText}</div>
                        <div class="dropdown-trigger" aria-labelledby={handleId}>
                            <portal-icon name="keyboard_arrow_down" size="16px"/>
                        </div>
                        <div class="dropdown-list" style={listCss}>
                            <ul role="listbox" id="listbox" aria-label={`dropdown-${this.$el.id}-list`}>
                                {
                                    this.dropdownOptionList?.map((item: DropdownOption) => {
                                        let index = -1;
                                        if (!item.disabled) {
                                            index = ++liIndex;
                                        }
                                        return (
                                            <li
                                                role="option"
                                                aria-label={item.ariaLabel ?? item.name}
                                                aria-selected={item.name === selectedValue}
                                                data-cy={this.getListItemId(item.id)}
                                                id={this.getListItemId(item.id)}
                                                data-item-id={item.id}
                                                onClick={(event: Event) => this.selectOption(event, item, index)}
                                                class={`dropdown-option ${item.disabled ? 'disabled' : ''} `}
                                            >
                                                {item.icon && <portal-icon class="option-icon" name={item.icon} size="20px"/>}
                                                <span class="option-label">{item.name}</span>
                                                {item.isCanaryTenant &&
                                            <portal-chip text={t('canary-tenant', getLanguage(store))}
                                                type="info" class="canary-chip" hideBorder/>}
                                            </li>
                                        );
                                    })
                                }
                            </ul>
                        </div>
                    </div>

                    <div class="p-hidden-accessible">
                        <input
                            type="text"
                            id={handleId}
                            data-cy={handleId}
                            readonly
                            disabled={this.disabled}
                            aria-labelledby={this.ariaLabelledby}
                            aria-label={this.ariaLabelledby ? undefined : `${this.ariaLabel} ${selectedValue ?? placeholderText}`}
                            value={selectedValue ? `${selectedValue} (${t('dropdown-selected', getLanguage(store))})` : ''}
                            onBlur={() => this.dropdownBlur()}
                            onFocus={() => this.dropdownFocus()}
                            onKeyDown={(ev) => this.dropdownKeyDown(ev)}
                        />
                    </div>
                </div>
            </Host>
        );
    }

    /**
     * Toogle the visibility of dropdown
     * @param ev
     */
    private toggleDropdown(ev: Event, fromKeyboard = false) {
        this.fromKeyboard = fromKeyboard;
        ev.stopPropagation();
        const currEl = ev.target as HTMLElement;

        const dropdownElContainer = currEl.closest('.portal-dropdown-container') as HTMLElement;
        const dropdownEl = dropdownElContainer.querySelector('.portal-dropdown') as HTMLElement;
        this.$dropdownEl = dropdownEl;
        const inputEl = dropdownElContainer.querySelector('input');
        inputEl?.focus();
        if (this.isDropdownOpen()) {
            this.closeDropdown();
        } else {
            this.getOptionList();
            dropdownEl.classList.add('dropdown-open');
            const listElem = this.$dropdownEl.querySelector('.dropdown-list') as HTMLElement;
            const focusedLi = this.$dropdownEl.querySelector('.option-selected') as HTMLElement;
            this.ensureOptionInView(listElem, focusedLi);
            inputEl?.setAttribute('aria-expanded', 'true');
            this.$el.shadowRoot?.querySelector('.portal-dropdown')?.setAttribute('aria-expanded', 'true');
        }
    }

    private getListItemId(id: string) {
        return `dropdown-${this.$el.id}-item-${id}`;
    }

    /**
     * This function is triggered when any option is selected. It sets the selected option and emits the `optionSelected` event.
     * @param event : event
     * @param selectedOption : selected option value/object
     * @param index : selected option index
     */
    private selectOption(event: Event, selectedOption: DropdownOption, index: number) {
        // necessary to differentiate the click on textfield vs dropdown list.
        event.stopImmediatePropagation();
        if (selectedOption) {
            // Set 'aria-selected' to false for previous selected li
            const ariaSelectedEl = this.$dropdownEl?.querySelector('li[aria-selected="true"]');
            if (this.$dropdownEl && ariaSelectedEl) {
                ariaSelectedEl.setAttribute('aria-selected', 'false');
                ariaSelectedEl.classList.remove('option-selected');
            }
            this.focusedMenuIndex = index;
            const selectedOptionLi = event.target as HTMLElement;
            this.selectedOption = selectedOption;
            this.optionSelected.emit(selectedOption);
            selectedOptionLi.classList.add('option-selected');
            selectedOptionLi.setAttribute('aria-selected', 'true');
            this.closeDropdown();
        }
    }

    /**
     * Get the Option list object to be used in keyboard navigation etc.
     */
    private getOptionList() {
        const liElems = this.$dropdownEl?.querySelectorAll('.dropdown-option:not(.disabled)');
        this.optionListObj = { ...liElems };
        this.setActiveOption();
    }

    /**
     * Set selected option as the active option
     */
    private setActiveOption() {
        for (const key in this.optionListObj) {
            // Set 'option-focused' class to the selected option from the list
            if (this.selectedOption && this.optionListObj[key].getAttribute('data-item-id') === this.selectedOption.id) {
                this.optionListObj[key].classList.add('option-selected');
                // Need to convert it to number as key in the object are string, and focusedMenuIndex is of type number
                this.focusedMenuIndex = Number(key);
                this.optionListObj[key].setAttribute('aria-selected', 'true');
            } else {
                this.optionListObj[key].classList.remove('option-selected');
                this.optionListObj[key].classList.remove('option-focused');
                this.optionListObj[key].setAttribute('aria-selected', 'false');
            }
        }
    }

    /**
     * Keyboard navigation function
     * @param ev Keyboard event
     */
    private dropdownKeyDown(ev: KeyboardEvent) {
        switch (ev.key) {
            case 'ArrowDown':
            case 'Down': // IE11
                if (this.isDropdownOpen()) {
                    ev.preventDefault();
                    this.focusedMenuIndex++;
                    if (this.focusedMenuIndex >= Object.keys(this.optionListObj).length) {
                        this.focusedMenuIndex = 0;
                    }
                    this.setOptionFocused(this.focusedMenuIndex);
                }
                break;

            case 'ArrowUp':
            case 'Up': // IE11
                if (this.isDropdownOpen()) {
                    ev.preventDefault();
                    this.focusedMenuIndex--;
                    if (this.focusedMenuIndex < 0) {
                        this.focusedMenuIndex = Object.keys(this.optionListObj).length - 1;
                    }
                    this.setOptionFocused(this.focusedMenuIndex);
                }
                break;

            case 'Spacebar':
            case ' ': // Some browsers bubble up the space keycode as this (' ' vs 'Space')
            case 'Enter': {
                ev.preventDefault();
                const currOption = this.getSelectedOption() as HTMLElement;
                if (this.isDropdownOpen() && currOption) {
                    currOption.click();
                } else {
                    this.toggleDropdown(ev, true);
                }
                break;
            }

            case 'Escape':
            case 'Esc': // IE11
            // Fall through
            case 'Tab':
                this.closeDropdown();
                break;
        }
    }

    private getHandleId() {
        return `dropdown-${this.$el.id}-input`;
    }

    /**
     * Function that will handle closing of dropdown
     */
    private closeDropdown() {
        this.optionListObj = {};
        if (!this.selectedOption) {
            this.focusedMenuIndex = -1;
        }
        if (this.dropdownState !== 'open') {
            this.$dropdownEl?.classList.remove('dropdown-open');
        }
        this.dropdownState = 'close';
        this.$dropdownEl?.querySelector('input')?.setAttribute('aria-expanded', 'false');
        this.$el.shadowRoot?.querySelector('.portal-dropdown')?.setAttribute('aria-activedescendant', '');
        this.$el.shadowRoot?.querySelector('.portal-dropdown')?.setAttribute('aria-expanded', 'false');
    }

    /**
     * Function to check if dropdown is open
     */
    private isDropdownOpen() {
        return this.$dropdownEl?.classList.contains('dropdown-open');
    }

    /**
     * Function to get the selected list item html element
     */
    private getSelectedOption() {
        return this.$dropdownEl?.querySelector('.dropdown-option.option-focused') as HTMLElement;
    }

    /**
     * Set 'option-focused' class to the selected option
     * @param currIndex
     */
    private setOptionFocused(currIndex: number) {
        const prevSelOption = this.getSelectedOption();
        if (prevSelOption) {
            prevSelOption.classList.remove('option-focused');
        }
        this.optionListObj[currIndex].classList.add('option-focused');
        const listElem = this.$dropdownEl?.querySelector('.dropdown-list') as HTMLElement;
        const focusedLi = this.$dropdownEl?.querySelector('.option-focused') as HTMLElement;
        this.$el.shadowRoot?.querySelector('.portal-dropdown')?.setAttribute('aria-activedescendant', focusedLi.id);

        const current = this.dropdownOptionList?.[currIndex];
        const input = (this.$el.shadowRoot?.querySelector('.portal-dropdown-container input') as HTMLInputElement);

        if (current && input) {
            const language = getLanguage(store);
            const selected = `(${t('dropdown-selected', language)})`;
            const notSelected = `(${t('dropdown-not-selected', language)})`;

            input.value = `${current.name} ${current === this.selectedOption ? selected : notSelected}`;
        }

        // Keep the focused option visible inside scrollbar
        this.ensureOptionInView(listElem, focusedLi);
    }

    /**
     * Function to ensure the focused li is visible inside scrollable list
     * @param container
     * @param element
     */
    private ensureOptionInView(container: HTMLElement, element: HTMLElement) {
        // Determine container top and bottom
        const cTop = container.scrollTop;
        const cBottom = cTop + container.clientHeight;

        // Determine element top and bottom
        const eTop = element?.offsetTop;
        const eBottom = eTop + element?.clientHeight;

        // Check if out of view
        if (eTop < cTop) {
            container.scrollTop -= (cTop - eTop);
        } else if (eBottom > cBottom) {
            container.scrollTop += (eBottom - cBottom);
        }
    }

    /**
     * Return name of selected option
     */
    private getSelectedValue() {
        // Check if the passed selected value exists in array.
        const contains = this.dropdownOptionList?.some(elem => JSON.stringify(this.selectedOption) === JSON.stringify(elem));
        return contains && this.selectedOption ? this.selectedOption.name : null;
    }

    /**
     * Funtion that will return placeholder text if supplied as prop else it will return default text.
     */
    private getPlaceholderText() {
        return this.placeholder ? this.placeholder : t('dropdown-placeholder', getLanguage(store));
    }

    /**
     * Set `dp-focused` class when dropdown is focused
     */
    private dropdownFocus() {
        if (!this.$dropdownEl?.classList.contains('dp-focused')) {
            this.$dropdownEl?.classList.add('dp-focused');
        }
        if (this.fromKeyboard && !this.$dropdownEl?.classList.contains('from-keyboard')) {
            this.$dropdownEl?.classList.add('from-keyboard');
        }
        if (!this.fromKeyboard && this.$dropdownEl?.classList.contains('from-keyboard')) {
            this.$dropdownEl?.classList.remove('from-keyboard');
        }

        this.fromKeyboard = true;
    }

    /**
     * Remove `dp-focused` class when dropdown is blurred
     */
    private dropdownBlur() {
        if (this.$dropdownEl?.classList.contains('dp-focused')) {
            this.$dropdownEl?.classList.remove('dp-focused');
            this.$el.shadowRoot?.querySelector('.portal-dropdown')?.setAttribute('aria-activedescendant', '');
            this.$el.shadowRoot?.querySelector('.portal-dropdown')?.setAttribute('aria-expanded', 'false');
        }
    }
}
